import { useEffect, useState } from 'react';

import { Heading, ModalDialog, Paragraph, Toast, withToast } from '@zillow/constellation';

import { useDispatch, useSelector } from 'react-redux';
import {
  inviteToApplyModalRequestClose,
  setInviteInfoFromLeadInquiry,
} from '../../../../../actions/invitationActionCreators';
import InvitationForm from './InvitationForm';
import {
  inviteToApplyModalIsOpenSelector,
  newInviteStatusSelector,
  propertyAddressSelector,
} from '../../../../../selectors';
import useQueryParams from '../../../../../hooks/useQueryParams';
import PropTypes from 'prop-types';
import { useListingUser } from '../../../../../hooks/useListingUser';
import { emitApplicantInviteEvent } from '../../../../../../client/analytics/clickstreamEvents';

export function getSuccessDisplayName(inviteInfo) {
  if (!inviteInfo.renterFirstName) {
    return inviteInfo.toEmail;
  } else if (!inviteInfo.renterLastName) {
    return inviteInfo.renterFirstName;
  } else {
    return `${inviteInfo.renterFirstName} ${inviteInfo.renterLastName}`;
  }
}

function Invitation({ enqueueToast, onSubmitAnalyticsCallback }) {
  const dispatch = useDispatch();
  const { user, listing } = useListingUser();
  const { leadId } = useQueryParams();
  const [displayName, setDisplayName] = useState('');
  const displayAddress = useSelector(propertyAddressSelector);
  const isOpen = useSelector(inviteToApplyModalIsOpenSelector);
  const newInviteStatus = useSelector(newInviteStatusSelector);

  useEffect(() => {
    dispatch(setInviteInfoFromLeadInquiry(leadId));
  }, [dispatch, leadId]);

  useEffect(() => {
    if (!displayName) {
      return;
    }
    if (newInviteStatus === 'success') {
      enqueueToast(
        <Toast
          id="invite-applicant-toast-success"
          appearance="success"
          body={`You have invited ${displayName} to apply to your rental.`}
        />,
      );
    } else if (newInviteStatus === 'failure') {
      enqueueToast(
        <Toast
          id="invite-applicant-toast-error"
          appearance="error"
          body="Unable to send application invite. This applicant may be already invited. Check and try again."
        />,
      );
    }
  }, [displayName, enqueueToast, newInviteStatus]);

  function onSubmit(values) {
    if (onSubmitAnalyticsCallback) {
      onSubmitAnalyticsCallback();
    } else {
      emitApplicantInviteEvent(listing, user);
    }

    setDisplayName(getSuccessDisplayName(values));
    dispatch(inviteToApplyModalRequestClose());
  }

  function onInviteModalClose() {
    dispatch(inviteToApplyModalRequestClose());
  }

  return (
    <ModalDialog
      className="Invitation__Modal"
      isOpen={isOpen}
      onClose={onInviteModalClose}
      header={<Heading level={6}>Send application link</Heading>}
      body={
        <>
          <Paragraph fontType="body" marginBottom="sm">
            Invite a potential tenant to apply to {displayAddress}.
          </Paragraph>
          <InvitationForm onSubmit={onSubmit} />
        </>
      }
    />
  );
}

Invitation.propTypes = {
  enqueueToast: PropTypes.func,
  onSubmitAnalyticsCallback: PropTypes.func,
};

export default withToast(Invitation);
